import { createPortal } from "react-dom";
import { useState, useEffect, useRef } from "react";
import useStore from "../../Store/Store";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";

interface ISourceDocControlButtons {
  setHeight: Function;
  setIsMaxSD: Function;
  isMaxSD: Boolean;
  setIsOpenSD: Function;
  setWidth: Function;
}
export const SourceDocControlButtons: React.FC<ISourceDocControlButtons> = (
  props: ISourceDocControlButtons
) => {
  const { setIsMaxSD, setIsOpenSD, isMaxSD } = props;
  const { setUserManuallyToggledSDMaximize } = useStore((state) => ({
    setUserManuallyToggledSDMaximize: state.setUserManuallyToggledSDMaximize,
  }));
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });

  // Reset corrupted isMaxSD state on component mount
  useEffect(() => {
    if (typeof isMaxSD !== "boolean") {
      setIsMaxSD(false);
    }
  }, []); // Run only on mount

  useEffect(() => {
    const updatePosition = () => {
      // Find sourceDoc container - use containerRef when available, fallback to querySelector
      let sourceDoc;
      if (containerRef.current) {
        sourceDoc = containerRef.current.closest(".sourceDocContainer");
      } else {
        // Fallback when containerRef is null (when isMaxSD is true)
        sourceDoc = document.querySelector(".sourceDocContainer");
      }

      if (sourceDoc) {
        const rect = sourceDoc.getBoundingClientRect();
        // Calculate the actual button width based on the current mode
        const buttonWidth = isMaxSD ? 35 : 30; // Match the width from the styling below

        // Calculate position relative to viewport
        if (isMaxSD) {
          // When maximized: Position button so its right edge touches the source doc's left edge
          setButtonPosition({
            top: rect.top + rect.height * 0.015,
            left: rect.left - buttonWidth, // Button's right edge will be at rect.left (no gap)
          });
        } else {
          // Normal position
          setButtonPosition({
            top: rect.top + rect.height * 0.015, // 1.5% from top of SourceDoc
            left: rect.left + rect.width * 0.032, // 3.2% from left of SourceDoc
          });
        }
      }
    };

    // Use requestAnimationFrame to ensure DOM has updated before calculating position
    const updatePositionWithDelay = () => {
      requestAnimationFrame(() => {
        setTimeout(updatePosition, 10); // Small delay to ensure DOM is updated
      });
    };

    // Initial position with delay
    updatePositionWithDelay();

    // Update on window events
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition);

    // Update when SourceDoc moves/resizes
    const observer = new ResizeObserver(updatePosition);
    const sourceDoc = document.querySelector(".sourceDocContainer"); // Always use querySelector for observer
    if (sourceDoc) {
      observer.observe(sourceDoc);
    }

    // Also observe for position changes
    const mutationObserver = new MutationObserver(updatePosition);
    if (sourceDoc) {
      mutationObserver.observe(sourceDoc, {
        attributes: true,
        attributeFilter: ["style"],
      });
    }

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [isMaxSD]);

  return (
    <>
      {!isMaxSD && (
        <div ref={containerRef}>
          <div className="SDMinimizeWrapper" onClick={() => setIsOpenSD(false)}>
            <div className="SDMinimize" />
          </div>
        </div>
      )}

      {/* Portal for maximize button when maximized */}
      {isMaxSD &&
        createPortal(
          <div
            id="sd-maximize-button-portal"
            style={{
              position: "fixed",
              top: `${buttonPosition.top}px`,
              left: `${buttonPosition.left}px`,
              height: isMaxSD ? "35px" : "15px",
              width: isMaxSD ? "35px" : "30px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10000001,
              cursor: "pointer",
              border: isMaxSD ? "none" : "1px solid #ffaea6",
              borderRadius: isMaxSD ? "50% 0 0 50%" : "25px",
              pointerEvents: "auto",
              backgroundColor: isMaxSD
                ? "#FFAEA7"
                : isHovered
                ? "#ffaea6"
                : "white",
              transition: "background-color 0.2s",
              // Make it look like half circle flipped horizontally for simulation mode (paper tab style)
              ...(isMaxSD && {
                borderTopRightRadius: "0",
                borderBottomRightRadius: "0",
                borderTopLeftRadius: "50%",
                borderBottomLeftRadius: "50%",
              }),
            }}
            onClick={() => {
              // if is running simulation, then close it then do the rest of the logic
              if (
                useStore.getState().simulationsState.isRunningSimulationNotifId
              ) {
                useStore.getState().setIsRunningSimulation(false);
                useStore.getState().setSelectedSimulationKey("");
              }
              setUserManuallyToggledSDMaximize(true);
              setIsMaxSD(false);
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {isMaxSD ? (
              // Show minimize icon in white for simulation mode
              <CloseFullscreenIcon
                style={{
                  color: "white",
                  fontSize: "20px",
                }}
              />
            ) : (
              // Show normal maximize icon for regular mode
              <div
                style={{
                  position: "relative",
                  height: isHovered ? "60%" : "50%",
                  width: "28%",
                  border: "1px solid #ffaea6",
                  borderRadius: "2px",
                  backgroundColor: isHovered ? "white" : "transparent",
                  transition: "height 0.2s",
                }}
              />
            )}
          </div>,
          document.body
        )}

      {/* Normal maximize button when not maximized */}
      {!isMaxSD && (
        <div
          className="SDMaximizeWrapper"
          id="sd-maximize-button"
          onClick={() => {
            if (typeof isMaxSD !== "boolean") {
              setIsMaxSD(true);
            } else {
              setIsMaxSD((prevIsMaxSD) => !prevIsMaxSD);
            }
          }}
        >
          <div className="SDMaximize" />
        </div>
      )}
    </>
  );
};
